import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { APPOINTMENT_STATUS, timeToMinutes } from "@/utils/calendarHelpers";

export const GRID_DAY_START = 9;
export const GRID_DAY_END = 18;
export const GRID_HOUR_HEIGHT = 96;

export const formatTimeRange = (start, end) => `${start} - ${end}`;

export const getGridEventStyle = (start, end, dayStart = GRID_DAY_START, hourHeight = GRID_HOUR_HEIGHT) => {
  const dayStartMin = dayStart * 60;
  const durationMin = Math.max(15, timeToMinutes(end) - timeToMinutes(start));
  const top = ((timeToMinutes(start) - dayStartMin) / 60) * hourHeight;
  const height = Math.max(68, (durationMin / 60) * hourHeight - 6);
  return { top: `${top + 2}px`, height: `${height}px`, minHeight: "68px" };
};

export const matchesDoctorId = (left, right) => String(left ?? "") === String(right ?? "");

export const getPatientName = (item) => {
  const raw = item?.patient ?? item?.patientName ?? item?.patient_name ?? "";
  const name = String(raw).trim();
  if (name && name !== "Bilinmeyen") return name;
  return name || "Bilinmeyen Hasta";
};

export const DoctorMultiSelect = ({
  doctors,
  values,
  onChange,
  isDark,
  inputClass,
  labelClass,
  className = "h-10 w-full lg:w-52",
}) => {
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(values.map(String));

  const toggle = (id) => {
    const sid = String(id);
    if (selectedSet.has(sid)) onChange(values.filter((v) => String(v) !== sid));
    else onChange([...values, sid]);
  };

  const label = values.length === 0
    ? "Tüm Doktorlar"
    : values.length === 1
      ? (doctors.find((d) => String(d.id) === values[0])?.name || "1 doktor")
      : `${values.length} doktor seçili`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className={`${className} justify-between text-xs font-normal ${inputClass}`}>
          <span className="truncate">{label}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={`w-[var(--radix-popover-trigger-width)] p-2 ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
        <div className="flex items-center justify-between mb-2 px-1">
          <span className={`text-[10px] font-medium ${labelClass}`}>Doktorlar</span>
          {values.length > 0 && (
            <button type="button" className="text-[10px] text-cyan-500" onClick={() => onChange([])}>Temizle</button>
          )}
        </div>
        <div className="max-h-52 overflow-y-auto space-y-0.5">
          {doctors.length === 0 ? (
            <p className={`text-xs px-2 py-1.5 ${labelClass}`}>Doktor yok</p>
          ) : doctors.map((doc) => (
            <label
              key={doc.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${isDark ? "hover:bg-slate-800/80" : "hover:bg-gray-50"}`}
            >
              <Checkbox checked={selectedSet.has(String(doc.id))} onCheckedChange={() => toggle(doc.id)} />
              <span className="truncate">{doc.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const CalendarEventCard = ({
  item,
  isDark,
  style,
  onClick,
  draggable,
  onDragStart,
}) => {
  const status = APPOINTMENT_STATUS[item.status] || APPOINTMENT_STATUS.other;
  const tone = isDark ? status.cardDark : status.card;
  const patient = getPatientName(item);
  const subtitle = item.note || item.typeName || item.statusName || status.label;

  return (
    <div
      className={`appt-cal-event appt-cal-v2-event ${tone} ${draggable ? "appt-cal-draggable" : ""}`}
      style={style}
      title={`${patient} · ${subtitle}`}
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
    >
      <div className="appt-cal-v2-event__head">
        <span className="appt-cal-v2-event__time">{formatTimeRange(item.start, item.end)}</span>
        <span className={`appt-cal-v2-event__dot ${status.dot}`} title={item.statusName || status.label} />
      </div>
      <p className="appt-cal-v2-event__patient">{patient}</p>
      <p className="appt-cal-v2-event__note">{subtitle}</p>
    </div>
  );
};

export const filterDoctorsBySelection = (doctors, doctorFilter) => {
  if (!doctorFilter?.length) return doctors;
  const set = new Set(doctorFilter.map(String));
  return doctors.filter((d) => set.has(String(d.id)));
};

export const filterAppointmentsByDoctors = (appointments, doctorFilter) => {
  if (!doctorFilter?.length) return appointments;
  const set = new Set(doctorFilter.map(String));
  return appointments.filter((a) => set.has(String(a.doctorId ?? "")));
};

export const buildGridStyle = (doctorCount, hourCount, hourHeight = GRID_HOUR_HEIGHT) => ({
  "--appt-doctor-count": Math.max(doctorCount, 1),
  "--appt-hour-count": hourCount,
  "--appt-hour-h": `${hourHeight}px`,
});
